import React, { useState } from "react";
import "./Auth.css";
import loginVec from "../../Assets/Images/signupVec.webp";
import brandLogo from "../../Assets/Images/brandLogo.png";
import { MdOutlineRemoveRedEye } from "react-icons/md";
import { MdOutlineVisibilityOff } from "react-icons/md";
import { Link, useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { postApiCaller } from "../../Libs/apiCaller";
import { toast } from "react-toastify";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { generateRSAKeyPair } from "../../utils/cryptoUtils";

const SignUp = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isTermsChecked, setIsTermsChecked] = useState(false);
  const navigate = useNavigate();

  const formik = useFormik({
    initialValues: {
      fullName: "",
      userName: "",
      email: "",
      phone: "",
      password: "",
      countryCode: "+1",
    },
    validationSchema: Yup.object({
      fullName: Yup.string()
        .test("no-blank", "Full name cannot be empty", (value) =>
          value?.trim() ? true : false
        )
        .max(30, "Full name cannot exceed 30 characters")
        .min(3, "Full name must be at least 3 characters")
        .required("Full name is required"),
      userName: Yup.string()
        .test("no-blank", "User name cannot be empty", (value) =>
          value?.trim() ? true : false
        )
        .max(30, "User name cannot exceed 30 characters")
        .min(3, "User name must be at least 3 characters")
        .required("User name is required")
        .test("is-valid-username", "User name is invalid", function (value) {
          const usernameRegex = /^[a-zA-Z0-9_.-]{3,}$/;
          return usernameRegex.test(value);
        }),
      email: Yup.string()
        .email("Invalid Email Address")
        .required("Email is required"),
      countryCode: Yup.string().required("Country code is required"),
      phone: Yup.string()
        .required("Phone number is required")
        .min(10, "Phone number must be 10 digits long")
        .max(15, "Phone number is too long"),
      password: Yup.string()
        .min(6, "Password must be at least 6 characters")
        .matches(/[A-Z]/, "Password must contain at least one uppercase letter")
        .matches(/[a-z]/, "Password must contain at least one lowercase letter")
        .matches(/[0-9]/, "Password must contain at least one number")
        .matches(
          /[!@#$%^&*(),.?":{}|<>]/,
          "Password must contain at least one special character"
        )
        .required("Password is required"),
    }),
    onSubmit: async (values) => {
      if (!isTermsChecked) {
        toast.error("Please accept the terms and conditions");
        return;
      }
      setIsLoading(true);
      const { publicKey, privateKey } = await generateRSAKeyPair();
      const payload = {
        name: values.fullName,
        phone: values.phone,
        countryCode: values.countryCode,
        role: "User",
        email: values.email,
        password: values.password,
        userName: values.userName,
        publicKey: publicKey,
        encryptedPrivateKey: privateKey,
      };
      const endPoint = "auth/register";
      const response = await postApiCaller(endPoint, payload);
      if (response?.status === 200) {
        toast.success("OTP sent successfully");
        navigate("/otp-verify-signup", {
          state: {
            countryCode: values.countryCode,
            phone: values.phone,
            id: response.data.user._id,
          },
        });
        formik.resetForm();
      } else {
        toast.error(response?.response?.data?.message || response.message);
      }
      setIsLoading(false);
    },
  });
  return (
    <section className="bg-login p-6 md:p-4 pb-16 sm:pb-0 min-h-screen flex items-center justify-center overflow-x-hidden">
      <div className="grid grid-cols-1 md:grid-cols-2 items-stretch w-full max-w-8xl mx-auto gap-0 md:gap-10">
        {/* Left Section (Image & Text) */}
        <div
          className="hidden md:grid relative grid-cols-1 h-full min-h-[calc(100vh-40px)] bg-cover bg-center rounded-xl"
          style={{ backgroundImage: `url(${loginVec})` }}
        >
          <div className="absolute inset-0 bg-black bg-opacity-20 rounded-xl"></div>

          <div className="absolute top-0 text-left text-white p-8 w-3/4">
            <img src={brandLogo} alt="Brand Logo" className="w-16 h-16 mb-2" />
          </div>
          <div className="">
            <div className="absolute bottom-4 mx-4 w-[calc(100%-2rem)] text-left rounded-lg text-white p-3 backdrop-blur-xs border border-white/30 bg-white/10">
              <p className="capitalize text-md">
                We believe communication should be simple, fast and private.
                Whether you're chatting with friends, connecting with family or
                collaborating with your team, our platform ensures seamless
                messaging and end-to-end security.
              </p>
            </div>
          </div>
        </div>

        {/* Right Section */}
        <div className="flex items-center justify-center px-4 md:px-0">
          <div className="w-full max-w-lg ">
            <h2 className="text-3xl capitalize leading-[50px] font-bold  text-gray-900 w-3/2 mb-2">
              Looks like you're new here!
            </h2>

            <p className="text-md text-gray-500 mb-6">
              Join us today and embark on an exciting adventure!
            </p>

            {/* Form */}
            <form onSubmit={formik.handleSubmit}>
              {/* Full Name Field */}
              <div className="mb-2">
                <label className="block text-gray-700 text-sm mb-2">
                  Full Name
                </label>
                <input
                  type="text"
                  name="fullName"
                  className="w-full bg-white px-4 py-3.5 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-600 fieldErr1"
                  placeholder="Enter Full Name"
                  value={formik.values.fullName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.fullName && formik.errors.fullName && (
                  <p className="text-red-500 text-[13px] errorMsg1">
                    {formik.errors.fullName}
                  </p>
                )}
              </div>

              <div className="mb-2">
                <label className="block text-gray-700 text-sm mb-2">
                  User Name
                </label>
                <input
                  type="text"
                  name="userName"
                  className="w-full bg-white px-4 py-3.5 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-600 fieldErr1"
                  placeholder="Enter User Name"
                  value={formik.values.userName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.userName && formik.errors.userName && (
                  <p className="text-red-500 text-[13px] errorMsg1">
                    {formik.errors.userName}
                  </p>
                )}
              </div>

              {/* Email Field */}
              <div className="mb-2">
                <label className="block text-gray-700 text-sm mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  name="email"
                  className="w-full bg-white px-4 py-3.5 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-600 fieldErr1"
                  placeholder="Enter Email Address"
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.email && formik.errors.email && (
                  <p className="text-red-500 text-[13px] errorMsg1">
                    {formik.errors.email}
                  </p>
                )}
              </div>

              <div className="mb-7">
                <label className="block text-gray-700 text-sm mb-2">
                  Phone Number
                </label>
                <div className="fieldErr1">
                  <PhoneInput
                    country={"us"}
                    value={
                      formik.values.countryCode.replace("+", "") +
                      formik.values.phone
                    }
                    onChange={(phone, data) => {
                      const dialCode = data?.dialCode || "";
                      const number = phone.slice(dialCode.length);

                      formik.setFieldValue("countryCode", `+${dialCode}`);
                      formik.setFieldValue("phone", number);
                    }}
                    containerClass={`!w-full ${
                      formik.touched.phone && formik.errors.phone
                        ? "fieldErr1"
                        : ""
                    }`}
                    inputProps={{
                      placeholder: "Enter phone number",
                      name: "phone",
                      autoFocus: false,
                    }}
                    inputClass={`!w-full !py-6 !pl-14 !pr-4 !rounded-lg !border-none !border-gray-300 !focus:ring-2 !focus:ring-purple-500 !focus:border-purple-500 !text-base ${
                      formik.touched.phone && formik.errors.phone
                        ? "fieldErr1"
                        : ""
                    }`}
                    buttonClass="!bg-transparent !border-none !outline-none !px-3"
                    dropdownClass="!z-50 border-left"
                    inputStyle={{ width: "100%" }}
                  />
                </div>

                {formik.touched.phone && formik.errors.phone && (
                  <p className="text-red-500 text-[13px] errorMsg1">
                    {formik.errors.phone}
                  </p>
                )}
              </div>

              {/* Password Field */}
              <div className="mb-2 relative">
                <label className="block text-gray-700 text-sm mb-2">
                  Password
                </label>
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  className="w-full bg-white px-4 py-3.5 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-600 fieldErr1 pr-10"
                  placeholder="Enter Password"
                  value={formik.values.password}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                <button
                  type="button"
                  className="absolute right-3 top-10 text-gray-500 hover:text-gray-700"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <MdOutlineRemoveRedEye size={22} />
                  ) : (
                    <MdOutlineVisibilityOff size={22} />
                  )}
                </button>
                {formik.touched.password && formik.errors.password && (
                  <p className="text-red-500 text-[13px] errorMsg1">
                    {formik.errors.password}
                  </p>
                )}
              </div>

              {/* Terms Checkbox */}
              <div className="flex justify-between items-center pt-2 mb-5">
                <label className="flex items-center text-sm text-gray-600">
                  <input
                    type="checkbox"
                    className="mr-2 h-4 w-4 rounded-sm border-[#aaa]"
                    checked={isTermsChecked}
                    onChange={(e) => setIsTermsChecked(e.target.checked)}
                  />
                  I agree to the
                  <Link
                    to="/terms"
                    className="text-blue-600 hover:underline mx-1"
                  >
                    Terms
                  </Link>
                  and
                  <Link
                    to="/privacy-policy"
                    className="text-blue-600 hover:underline mx-1"
                  >
                    Privacy Policy.
                  </Link>
                </label>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                className="w-full bg-[var(--primary-color)] text-white py-3 rounded-full text-lg font-medium hover:bg-purple-500 transition"
                disabled={isLoading}
              >
                {isLoading ? "Loading..." : "Sign Up"}
              </button>
            </form>
            <div className="flex items-center my-4">
              <div className="w-full border-t border-[#A3AEBE] border-dashed"></div>
              <span className="px-3 text-gray-500">Or</span>
              <div className="w-full border-t border-[#A3AEBE] border-dashed"></div>
            </div>

            {/* Sign Up Link */}
            <p className="text-center text-gray-600 text-md mt-8">
              Already have an account?
              <Link
                to="/sign-in"
                className="text-purple-600  mx-1 font-semibold hover:underline"
              >
                Login
              </Link>
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SignUp;
